import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { environment } from '@env/environment';
import { AuthService } from '@services/auth.service';

/**
 * Токен в Authorization, а на истёкшей сессии — переход на вход Keycloak с возвратом в тот же раздел.
 *
 * Обрабатываем только адреса API: клиент входа ходит в token-endpoint Keycloak через тот же
 * HttpClient, и без ограничения по префиксу его 401 уводил бы в бесконечное обновление токена.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
    if (!req.url.startsWith(environment.apiUrl)) {
        return next(req);
    }

    const auth = inject(AuthService);
    const token = auth.getAccessTokenNow();

    if (!token) {
        // Токена нет: запрос без заголовка вернул бы 401, и пользователь увидел бы ошибку на
        // странице. Сразу уходим на вход с возвратом в текущий раздел.
        auth.endSession();

        return throwError(() => noToken(req.url));
    }

    return next(withToken(req, token)).pipe(
        catchError((error: unknown) => {
            if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
                return throwError(() => error);
            }

            // 401 на живом токене: обычно он истёк минуту назад, а silentRenew ещё не успел
            // (в фоновой вкладке таймеры идут медленно). Обновляем токен и повторяем запрос один раз.
            // Обновление не вышло — refreshOnce() сам зовёт endSession(), повтор не делаем.
            return auth
                .refreshOnce()
                .pipe(switchMap((freshToken) => next(withToken(req, freshToken))));
        }),
    );
};

function withToken<T>(req: HttpRequest<T>, token: string): HttpRequest<T> {
    return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

function noToken(url: string): HttpErrorResponse {
    return new HttpErrorResponse({ status: 401, statusText: 'Нет access-токена', url });
}
